(() => {
  class CollectionContrastGrid extends HTMLElement {
    connectedCallback() {
      this.observer?.disconnect();
      this.cards = Array.from(
        this.querySelectorAll(
          '.palo-collection-card[data-text-color-mode="auto"][data-auto-enabled="true"]'
        )
      );
      this.observeCards();
    }

    observeCards() {
      if (!this.cards.length) return;

      if (!('IntersectionObserver' in window)) {
        this.cards.forEach((card) => this.prepareCard(card));
        return;
      }

      this.observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.prepareCard(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '180px 0px' }
      );

      this.cards.forEach((card) => this.observer.observe(card));
    }

    prepareCard(card) {
      const image = card.querySelector('.palo-collection-card__image');

      if (!image) {
        this.applyFallback(card);
        return;
      }

      if (image.complete && image.naturalWidth) {
        this.detectContrast(card, image);
      } else {
        image.addEventListener('load', () => this.detectContrast(card, image), { once: true });
        image.addEventListener('error', () => this.applyFallback(card), { once: true });
      }
    }

    detectContrast(card, image) {
      try {
        const sampleWidth = Math.min(80, image.naturalWidth);
        const sampleHeight = Math.min(80, image.naturalHeight);
        const canvas = document.createElement('canvas');
        canvas.width = sampleWidth;
        canvas.height = sampleHeight;

        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) {
          this.applyFallback(card);
          return;
        }

        const sourceX = 0;
        const sourceY = Math.max(0, image.naturalHeight - Math.round(image.naturalHeight * 0.34));
        const sourceWidth = Math.max(1, Math.round(image.naturalWidth * 0.46));
        const sourceHeight = Math.max(1, Math.round(image.naturalHeight * 0.34));

        context.drawImage(
          image,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          sampleWidth,
          sampleHeight
        );

        const pixels = context.getImageData(0, 0, sampleWidth, sampleHeight).data;
        let luminanceTotal = 0;
        let countedPixels = 0;

        for (let index = 0; index < pixels.length; index += 16) {
          const alpha = pixels[index + 3] / 255;
          if (alpha < 0.4) continue;

          const red = this.toLinear(pixels[index] / 255);
          const green = this.toLinear(pixels[index + 1] / 255);
          const blue = this.toLinear(pixels[index + 2] / 255);

          luminanceTotal += (0.2126 * red + 0.7152 * green + 0.0722 * blue) * alpha;
          countedPixels += 1;
        }

        if (!countedPixels) {
          this.applyFallback(card);
          return;
        }

        const luminance = luminanceTotal / countedPixels;
        const threshold = Number(card.dataset.autoThreshold || 50) / 100;
        const useLightText = luminance < threshold;

        card.dataset.contrast = useLightText ? 'light' : 'dark';
        card.style.setProperty('--palo-card-text-color', useLightText ? '#ffffff' : '#111111');
      } catch (error) {
        this.applyFallback(card);
      }
    }

    toLinear(value) {
      return value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
    }

    applyFallback(card) {
      const fallback = card.dataset.fallbackColor || '#ffffff';
      card.style.setProperty('--palo-card-text-color', fallback);
      card.dataset.contrast = this.isLightColor(fallback) ? 'light' : 'dark';
    }

    isLightColor(color) {
      const value = color.replace('#', '').trim();
      const normalized = value.length === 3
        ? value.split('').map((character) => character + character).join('')
        : value;

      if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return true;

      const red = parseInt(normalized.slice(0, 2), 16);
      const green = parseInt(normalized.slice(2, 4), 16);
      const blue = parseInt(normalized.slice(4, 6), 16);
      return (red * 299 + green * 587 + blue * 114) / 1000 > 150;
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }

  if (!customElements.get('collection-contrast-grid')) {
    customElements.define('collection-contrast-grid', CollectionContrastGrid);
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('collection-contrast-grid').forEach((grid) => {
      if (typeof grid.connectedCallback === 'function') grid.connectedCallback();
    });
  });
})();
